"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { MediaPlayerInstance } from "@vidstack/react";

const MS_PER_FRAME = 41;

export function usePlaybackState({
  isBusinessCardPlaying,
}: {
  isBusinessCardPlaying: boolean;
}) {
  const [playerInstance, setPlayerInstance] =
    useState<MediaPlayerInstance | null>(null);
  const [isPaused, setIsPaused] = useState(true);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [skipToTime, setSkipToTime] = useState(-1);
  const [audioVolumePercent, setAudioVolumePercent] = useState(100);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isPlayerWaiting, setIsPlayerWaiting] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [autoplayState, setAutoplayState] = useState(true);
  const [videoCanplay, setVideoCanPlay] = useState(false);
  const [videoJsOptions, setVideoJsOptions] = useState<
    { src: string | undefined; type: string | undefined }[]
  >([{ src: undefined, type: "application/x-mpegURL" }]);

  // A session picked from the timeline starts playing once its own stream can
  // play. Calling play() at the click would hit the outgoing source.
  const playWhenReadyRef = useRef(false);
  const requestPlayWhenReady = useCallback(() => {
    playWhenReadyRef.current = true;
  }, []);
  const consumePlayWhenReady = useCallback(() => {
    const requested = playWhenReadyRef.current;
    playWhenReadyRef.current = false;
    return requested;
  }, []);

  const frameForward = () =>
    setCurrentPlaybackTime((prev) => prev + MS_PER_FRAME);
  const frameBack = () =>
    setCurrentPlaybackTime((prev) => prev - MS_PER_FRAME);

  useEffect(() => {
    if (!isPaused && !isBusinessCardPlaying && playerInstance?.state.canPlay) {
      playerInstance.play()?.catch(() => {});
    }
  }, [isPaused, playerInstance, isBusinessCardPlaying]);

  useEffect(() => {
    if (
      playerInstance != null &&
      Math.floor(playerInstance.volume * 100) !== audioVolumePercent
    ) {
      playerInstance.setAudioGain(audioVolumePercent / 100);
    }
  }, [audioVolumePercent, playerInstance]);

  return {
    playerInstance,
    setPlayerInstance,
    isPaused,
    setIsPaused,
    currentPlaybackTime,
    setCurrentPlaybackTime,
    skipToTime,
    setSkipToTime,
    audioVolumePercent,
    setAudioVolumePercent,
    isAudioMuted,
    setIsAudioMuted,
    isPlayerWaiting,
    setIsPlayerWaiting,
    isSeeking,
    setIsSeeking,
    autoplayState,
    setAutoplayState,
    videoCanplay,
    setVideoCanPlay,
    videoJsOptions,
    setVideoJsOptions,
    frameForward,
    frameBack,
    requestPlayWhenReady,
    consumePlayWhenReady,
  };
}
